<template>
    <div>
        <Dropdown
            placement="bottom-start"
            trigger="custom"
            :visible="dropdownIsVisible"
            @onClickoutside="toggleDropdown"
        >
            <hox-dropdown-button
                :is-active="dropdownIsVisible"
                :is-disabled="isDisabled || isInProgress"
                :is-loading="isSettingPublishedStatus"
                data-testid="library__publish-status-select"
                @click="toggleDropdown"
            >
                <slot>Publish</slot>
            </hox-dropdown-button>
            <template #list>
                <DropdownItem
                    data-testid="library-doubleclick--publish"
                    @click="setPublishedStatus(PLATFORM.DOUBLECLICK, true)"
                >
                    Publish to {{ PlatformLabel[PLATFORM.DOUBLECLICK] }} feed
                </DropdownItem>
                <DropdownItem
                    data-testid="library-doubleclick--unpublish"
                    @click="setPublishedStatus(PLATFORM.DOUBLECLICK, false)"
                >
                    Unpublish from {{ PlatformLabel[PLATFORM.DOUBLECLICK] }} feed
                </DropdownItem>
                <DropdownItem data-testid="library-doubleclick--rebuild" @click="rebuild(PLATFORM.DOUBLECLICK)">
                    Rebuild {{ PlatformLabel[PLATFORM.DOUBLECLICK] }} feed
                </DropdownItem>
                <DropdownItem divided data-testid="library-opendc--publish" @click="rebuild(PLATFORM.OPENDC)">
                    Render ads for {{ PlatformLabel[PLATFORM.OPENDC] }}
                </DropdownItem>
                <DropdownItem
                    data-testid="library-opendc--unpublish"
                    :disabled="!showPublishToOpendc"
                    @click="showPublishToOpendc && publishCOAdset()"
                >
                    Publish ads to {{ PlatformLabel[PLATFORM.OPENDC] }} adset
                </DropdownItem>
                <DropdownItem
                    v-if="userCanPublishToTikTok"
                    divided
                    data-testid="library-tiktok--publish"
                    @click="publishToTikTok"
                >
                    Publish to {{ PlatformLabel[PLATFORM.TIKTOK] }}
                </DropdownItem>

                <DropdownItem
                    v-if="userCanPublishToFacebook"
                    data-testid="library-facebook--publish"
                    @click="showPublishModal(PLATFORM.FACEBOOK, 'publish')"
                >
                    Publish to {{ PlatformLabel[PLATFORM.FACEBOOK] }}
                </DropdownItem>
            </template>
        </Dropdown>
        <hox-modal v-if="rebuildFeedModal" @close="clearRebuildFeedCallback">
            <template #header>Rebuild feed</template>
            <div class="rebuild-modal__info">Are you sure?</div>
            <div>
                Rebuilding the feed will unpublish existing deliverables matching the sizes of the current selection.
                The current selection will then be published to build a new feed for each size selected.
            </div>

            <template #footer>
                <Button @click="clearRebuildFeedCallback">Close</Button>
                <Button type="error" @click="rebuildFeedCallback">Rebuild</Button>
            </template>
        </hox-modal>
    </div>
</template>

<script lang="ts">
import type { PropType } from "vue";

import { DOWNLOAD_FORMAT, PLATFORM, type PublishProvidersType } from "hoxton-shared/enums/exports";

import HoxModal from "@/components/Modal/Modal/Modal.vue";
import HoxDropdownButton from "@/components/common/DropdownButton.vue";
import { JobStatusToNotificationStatus } from "@/enums/jobs";
import { NotificationStatus, NotificationTypes } from "@/enums/notifications";
import { PlatformLabel } from "@/enums/platforms";
import {
    downloadJob,
    publishChoreographFeed,
    publishDeliverables,
    unpublishDeliverables
} from "@/services/deliverables/publish";
import { AuthGetters } from "@/store/modules/auth";
import { JobsAction } from "@/store/modules/jobs";
import { NotificationsAction } from "@/store/modules/notifications";

import type { ConstructedDeliverable, Err, LibraryFilters } from "@/types/types";

export default {
    components: {
        HoxModal,
        HoxDropdownButton
    },

    props: {
        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },

        isDisabled: {
            required: true,
            type: Boolean
        },

        libraryFiltersForQuery: {
            required: false,
            type: Object as PropType<LibraryFilters>
        },

        itemToChangeNumber: {
            required: false,
            type: Number
        },

        showPublishToOpendc: {
            required: false,
            type: Boolean
        },

        lastJob: {
            required: false,
            type: Object,
            default: null
        }
    },

    emits: ["showPublishModal", "publishedStatusHasBeenSet"],

    data() {
        return {
            dropdownIsVisible: false,
            isSettingPublishedStatus: false,
            rebuildFeedModal: false,
            rebuildFeedCallback: null as unknown as null | (() => void),
            PLATFORM,
            PlatformLabel
        };
    },

    computed: {
        campaignId() {
            return this.$route.params.campaignId as string;
        },
        deliverablesCount() {
            return this.itemToChangeNumber || this.deliverables.length;
        },

        isCampaignLocked() {
            return this.$store.state.campaign.isLocked;
        },

        isInProgress() {
            return this.isSettingPublishedStatus || this.isCampaignLocked;
        },

        isSuperAdmin() {
            return this.$store.getters[AuthGetters.isSuperAdmin];
        },

        userCanPublishToFacebook() {
            const { fbAdsManagerConfig } = this.$store.state.campaign.client;
            return fbAdsManagerConfig && this.isSuperAdmin;
        },

        userCanPublishToTikTok() {
            return this.isSuperAdmin;
        }
    },

    methods: {
        clearRebuildFeedCallback() {
            this.rebuildFeedModal = false;
            this.rebuildFeedCallback = null;
        },

        showPublishModal(platform: PublishProvidersType, action: "publish" | "unpublish" | "rebuild") {
            this.$emit("showPublishModal", {
                platform,
                action
            });
            this.toggleDropdown();
        },

        rebuild(platform: PublishProvidersType) {
            this.rebuildFeedModal = true;
            this.rebuildFeedCallback = () => {
                this.setPublishedStatus(platform, true, true);
                this.clearRebuildFeedCallback();
            };
        },

        async setPublishedStatus(platform: PublishProvidersType, isPublishing: boolean, rebuild?: boolean) {
            this.isSettingPublishedStatus = true;

            const adsText = `${this.deliverablesCount} ${this.deliverablesCount === 1 ? "ad" : "ads"}`;
            const addingOrRemoving = isPublishing ? "Adding" : "Removing";
            const toOrFrom = isPublishing ? "to" : "from";

            this.$snackbar.info(
                `${addingOrRemoving} ${adsText} ${toOrFrom} ${this.PlatformLabel[platform as keyof typeof this.PlatformLabel]} feeds`
            );
            this.toggleDropdown();

            let job = null;

            try {
                if (isPublishing) {
                    job = await publishDeliverables(
                        this.campaignId,
                        platform,
                        this.deliverables,
                        this.libraryFiltersForQuery,
                        rebuild
                    );
                } else {
                    job = await unpublishDeliverables(
                        this.campaignId,
                        platform,
                        this.deliverables,
                        this.libraryFiltersForQuery
                    );
                }
            } catch (err) {
                this.$snackbar.error((err as Err).message);
                return;
            } finally {
                this.isSettingPublishedStatus = false;
            }

            const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                deliverablesCount: this.deliverablesCount,
                platform,
                status: NotificationStatus.InProgress,
                type: isPublishing ? NotificationTypes.Publish : NotificationTypes.Unpublish,
                job
            });
            this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                jobId: job?._id,
                notificationId
            });
            this.$emit("publishedStatusHasBeenSet");
        },

        async publishToTikTok() {
            try {
                const job = await downloadJob(
                    this.campaignId,
                    DOWNLOAD_FORMAT.TIKTOK,
                    this.deliverables,
                    [],
                    this.libraryFiltersForQuery
                );

                const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                    message: job.message,
                    status: JobStatusToNotificationStatus[job.status],
                    type: NotificationTypes.DownloadJob,
                    job
                });
                this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                    jobId: job._id,
                    notificationId
                });
                this.$snackbar.info(
                    "Your ads are being prepared for publish to TikTok Ads Manager. Take a look at your notifications to check on progress"
                );
            } catch {
                this.$snackbar.error(
                    "There was an unexpected error while preparing your ads for publish to TikTok Ads Manager. Please try again in a few moments"
                );
            }
        },

        toggleDropdown() {
            this.dropdownIsVisible = !this.dropdownIsVisible;
        },

        async publishCOAdset() {
            const adsText = `${this.deliverablesCount} ${this.deliverablesCount === 1 ? "ad" : "ads"}`;
            this.isSettingPublishedStatus = true;

            this.$snackbar.info(`Adding ${adsText} to ${PlatformLabel[PLATFORM.OPENDC]} adset`);
            this.toggleDropdown();

            try {
                const uniqueIdsList = this.deliverables.map(
                    deliverable => `${deliverable.reportingLabel.replace(/ /g, "_").toLowerCase()}_${deliverable.hash}`
                );
                await publishChoreographFeed(this.lastJob._id, uniqueIdsList);
            } catch (err) {
                this.$snackbar.error((err as Err).message);
                return;
            } finally {
                this.isSettingPublishedStatus = false;
            }

            const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                deliverablesCount: this.deliverablesCount,
                platform: PLATFORM.OPENDC,
                status: NotificationStatus.InProgress,
                type: NotificationTypes.Publish,
                job: this.lastJob
            });
            this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                jobId: this.lastJob._id,
                notificationId
            });
            this.$emit("publishedStatusHasBeenSet");
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;

.rebuild-modal__info {
    padding-bottom: $spacing-smaller;
    font-weight: bold;
}
</style>
